/* eslint max-lines-per-function: ["error", 180]*/
import React, { useMemo, useState } from 'react';
import { Card, Form } from 'react-bootstrap';

import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useNavigation } from '../../../common/navigation';
import { MailTeamModal } from '../../components/MailTeam/MailTeamModal';
import { SalesInvoicingSearch } from '../../components/SalesInvoicing/SalesInvoicingSearch';
import FormRow from '../../../app/components/FormRow';
import { NewDatePickerReact } from '../../../app/components/NewDatePickerReact';
import { useAuth } from '../../../common/hooks/useAuth';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';
import { ResponseError } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { TooltipKey } from '../../../common/pokCore/tooltip.enum';
import { CardTitleWithInfo } from '../../../app/components/CardTitleWithInfo';
import { SalesInvoicingInfo } from '../../../translations/instructions/SalesInvoicingInfo';

export const SalesInvoicingView: React.FC = () => {
  const pok = usePokCore();
  const nav = useNavigation();
  const auth = useAuth();
  const { t, tk } = useTranslation('fvsLng');
  const [show, setShow] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [filtersRefresh, setFiltersRefresh] = useState(false);
  const [invoiced, setInvoiced] = useState(true);
  const [waiting, setWaiting] = useState<boolean>(false);
  const notifications = useNotifications();

  const setDate = (date: Date | undefined) => {
    if (date) {
      //ten kalendarz inaczej ustawia godzinę niż nasz główny kalendarz
      //dlatego trzeba skorygować godzinę
      date.setMinutes(-date.getTimezoneOffset());
      return date;
    }
    return undefined;
  };

  const [month, setMonth] = useState<Date | undefined>(
    setDate(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 20)),
  );

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.estimatesByMonth.getSalesInvoicing(
          t,
          tk,
          refresh,
          setRefresh,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
          invoiced,
          month,
        )) as GridGetterFunction,
    [pok.estimatesByMonth, t, tk, invoiced, month, refresh],
  );

  const getCount = useMemo(
    () =>
      ((filterText, filterColumns) =>
        pok.estimatesByMonth.getSalesInvoicingCount(
          filterText,
          filterColumns,
          invoiced,
          month,
        )) as GridCountFunction,
    [pok.estimatesByMonth, invoiced, month],
  );

  const handleClick = async (id: string) => {
    const ebm = await pok.estimatesByMonth.getById(id);
    return nav.projectDetails(ebm.project.id, true, 'estimates');
  };

  const handleMail = () => {
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
  };

  const exportToXls = async () => {
    setWaiting(true);
    const currentDate = new Date().toISOString().split('T')[0];
    try {
      await pok.reports.reportExcelForSalesInvoicing(
        `${t(tk.finance.salesInvoicingFileName)}_${currentDate}.xlsx`,
        invoiced,
        month,
      );
      setWaiting(false);
    } catch (error) {
      notifications.caughtError(error as ResponseError);
      setWaiting(false);
    }
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <CardTitleWithInfo title={t(tk.finance.salesInvoicing)}>
          <SalesInvoicingInfo />
        </CardTitleWithInfo>
        <Form className="d-grid gap-4 mb-3">
          <FormRow
            controlId="month"
            label={t(tk.common.month)}
            tooltipKey={TooltipKey.salesInvoicingMonthTooltip}
          >
            <div className="small-calendar">
              <NewDatePickerReact
                onChange={value => {
                  setMonth(setDate(value as Date | undefined));
                  setFiltersRefresh(!filtersRefresh);
                }}
                value={month}
                required={true}
              />
            </div>
          </FormRow>
          <FormRow
            controlId="invoiced"
            label={t(tk.finance.hideInvoiced)}
            tooltipKey={TooltipKey.salesInvoicingHideInvoicedTooltip}
          >
            <Form.Check
              type="checkbox"
              className="pt-2"
              checked={invoiced}
              onChange={e => {
                setInvoiced(e.target.checked);
                setFiltersRefresh(!filtersRefresh);
              }}
            />
          </FormRow>
        </Form>
        {auth.check(PermissionsEnum.Finance) && (
          <Button
            className="mb-4"
            variant="outline-primary"
            onClick={handleMail}
          >
            {t(tk.attachment.sendMail)}
          </Button>
        )}
        <SalesInvoicingSearch
          getData={getData}
          getCount={getCount}
          onRowClick={handleClick}
          refresh={refresh}
          additionalFiltersRefresh={filtersRefresh}
        />

        <Button
          variant="outline-secondary"
          onClick={exportToXls}
          isLoading={waiting}
        >
          {t(tk.button.export)}
        </Button>
        <MailTeamModal
          show={show}
          handleClose={handleClose}
          tv={false}
          invoiced={invoiced}
          month={month}
          sendFunction2={pok.emails.sendSalesInvoicingEmail}
        />
      </Card.Body>
    </Card>
  );
};
